import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import partyService from '../../services/partyService';
import masterService from '../../services/masterService';
import { b2bService } from '../../services/b2bService';
import MasterPageWrapper from './common/MasterPageWrapper';
import MasterToolbar from './components/MasterToolbar';
import MasterTable from './components/MasterTable';
import StatusBadge from './components/StatusBadge';
import ActionMenu from './components/ActionMenu';
import ConfirmModal from './components/ConfirmModal';
import usePagination from '../../hooks/usePagination';
import './PartyMasterPage.css';

const INITIAL_PARTY = {
  partyId: 0,
  partyCode: '',
  partyName: '',
  classificationId: '',
  defaultPanelId: '',
  contactPerson: '',
  contactMobile: '',
  contactEmail: '',
  address: '',
  city: '',
  state: '',
  gstNumber: '',
  panNumber: '',
  creditLimit: 0.00,
  paymentTermsDays: 0,
  creditPolicy: 'HYBRID',
  breachAction: 'BLOCK_REGISTRATION',
  gracePeriodDays: 7,
  isActive: true,
  allowPortalRegistration: true,
  // Client Portal Credentials
  enablePortalAccess: false,
  portalUsername: '',
  portalPassword: '',
  portalRole: 'ADMIN',
  // Co-Branded Reports Settings
  enableCoBranding: false,
  coBrandingLogoUrl: '',
  coBrandingTagline: '',
  coBrandingAddress: '',
  coBrandingContact: '',
  coBrandingHeaderType: 'CO_BRANDED'
};

export const PartyMasterPage = ({ showToast: propShowToast }) => {
  const navigate = useNavigate();

  // Stable Toast reference to prevent re-render / callback loops
  const showToastRef = useRef(propShowToast || (() => { }));
  useEffect(() => {
    showToastRef.current = propShowToast || (() => { });
  }, [propShowToast]);

  const triggerToast = useCallback((msg, type = 'info') => {
    if (typeof showToastRef.current === 'function') {
      showToastRef.current(msg, type);
    }
  }, []);

  const [viewMode, setViewMode] = useState('list');
  const [isEditMode, setIsEditMode] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);

  // Form State
  const [formData, setFormData] = useState(INITIAL_PARTY);
  const [errors, setErrors] = useState({});
  const [showPortalPassword, setShowPortalPassword] = useState(false);

  // Lookups
  const [classifications, setClassifications] = useState([]);
  const [panels, setPanels] = useState([]);

  // List State
  const [parties, setParties] = useState([]);
  const [loadingParties, setLoadingParties] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [classFilter, setClassFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  // Quick Portal Credential Modal State
  const [credModalTarget, setCredModalTarget] = useState(null);
  const [modalUsername, setModalUsername] = useState('');
  const [modalPassword, setModalPassword] = useState('');
  const [modalRole, setModalRole] = useState('ADMIN');
  const [modalShowPassword, setModalShowPassword] = useState(false);
  const [savingCreds, setSavingCreds] = useState(false);

  // Financial Ledger (Khata) Modal State
  const [ledgerModalTarget, setLedgerModalTarget] = useState(null);
  const [ledgerLoading, setLedgerLoading] = useState(false);
  const [ledgerData, setLedgerData] = useState(null);
  const [ledgerFromDate, setLedgerFromDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 60);
    return d.toISOString().split('T')[0];
  });
  const [ledgerToDate, setLedgerToDate] = useState(() => new Date().toISOString().split('T')[0]);

  // Advance Deposit / Wallet Recharge Modal State
  const [advanceModalTarget, setAdvanceModalTarget] = useState(null);
  const [advanceAmount, setAdvanceAmount] = useState(20000);
  const [advanceMode, setAdvanceMode] = useState('UPI');
  const [advanceRef, setAdvanceRef] = useState('');
  const [advanceBank, setAdvanceBank] = useState('');
  const [advanceNotes, setAdvanceNotes] = useState('Advance deposit for quota recharge');
  const [savingAdvance, setSavingAdvance] = useState(false);

  // Centralized Pagination
  const { paginatedItems: pagedParties, paginationProps: partyPaginationProps } = usePagination({
    items: parties,
    resetDeps: [searchQuery, classFilter, statusFilter]
  });

  // Confirm Modal
  const [confirmTarget, setConfirmTarget] = useState(null);

  // Load Lookups
  const loadLookups = useCallback(async () => {
    try {
      const [classRes, panelsRes] = await Promise.all([
        partyService.getClassifications({ isActive: true }),
        masterService.getPanels({ isActive: true })
      ]);

      if (classRes && classRes.success) {
        setClassifications(classRes.data || []);
      }
      if (panelsRes && panelsRes.success) {
        setPanels(panelsRes.data || []);
      }
    } catch (err) {
      console.error('Error loading party lookups:', err);
    }
  }, []);

  // Load Parties — safe without loop
  const loadParties = useCallback(async () => {
    setLoadingParties(true);
    try {
      const params = {};
      if (statusFilter !== 'all') {
        params.isActive = statusFilter === 'active';
      }
      if (searchQuery.trim()) {
        params.search = searchQuery.trim();
      }
      if (classFilter) {
        params.classificationId = parseInt(classFilter, 10);
      }

      const res = await partyService.getParties(params);
      if (res && res.success) {
        setParties(res.data || []);
      }
    } catch (err) {
      console.error('Error loading parties:', err);
      triggerToast(err.response?.data?.message || 'Failed to load parties', 'error');
    } finally {
      setLoadingParties(false);
    }
  }, [searchQuery, classFilter, statusFilter, triggerToast]);

  useEffect(() => {
    loadLookups();
  }, [loadLookups]);

  useEffect(() => {
    if (viewMode === 'list') {
      loadParties();
    }
  }, [viewMode, loadParties]);

  // Add / Edit Handlers
  const handleAddNew = () => {
    setFormData(INITIAL_PARTY);
    setErrors({});
    setIsEditMode(false);
    setShowPortalPassword(false);
    setViewMode('form');
  };

  const handleEdit = (party) => {
    setIsEditMode(true);
    setShowPortalPassword(false);
    setFormData({
      partyId: party.partyId,
      partyCode: party.partyCode || '',
      partyName: party.partyName || '',
      classificationId: party.classificationId || '',
      defaultPanelId: party.defaultPanelId || '',
      contactPerson: party.contactPerson || '',
      contactMobile: party.contactMobile || '',
      contactEmail: party.contactEmail || '',
      address: party.address || '',
      city: party.city || '',
      state: party.state || '',
      gstNumber: party.gstNumber || '',
      panNumber: party.panNumber || '',
      creditLimit: party.creditLimit || 0,
      paymentTermsDays: party.paymentTermsDays || 0,
      creditPolicy: party.creditPolicy || 'HYBRID',
      breachAction: party.breachAction || 'BLOCK_REGISTRATION',
      gracePeriodDays: party.gracePeriodDays ?? 7,
      isActive: party.isActive,
      allowPortalRegistration: party.allowPortalRegistration !== false,
      // Portal properties
      enablePortalAccess: party.hasPortalAccess || Boolean(party.portalUsername),
      portalUsername: party.portalUsername || party.partyCode?.toLowerCase() || '',
      portalPassword: '',
      portalRole: party.portalRole || 'ADMIN',
      // Co-Branded Reports Settings
      enableCoBranding: Boolean(party.enableCoBranding),
      coBrandingLogoUrl: party.coBrandingLogoUrl || '',
      coBrandingTagline: party.coBrandingTagline || '',
      coBrandingAddress: party.coBrandingAddress || party.address || '',
      coBrandingContact: party.coBrandingContact || party.contactMobile || '',
      coBrandingHeaderType: party.coBrandingHeaderType || 'CO_BRANDED'
    });
    setErrors({});
    setViewMode('form');
  };

  // Co-Branding Logo File Upload Handler
  const handleLogoFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 2 * 1024 * 1024) {
      triggerToast('Logo file size exceeds 2 MB limit.', 'error');
      return;
    }

    const ext = file.name.split('.').pop()?.toLowerCase();
    if (!['png', 'jpg', 'jpeg', 'webp'].includes(ext)) {
      triggerToast('Only PNG, JPG, JPEG, and WebP images are allowed. SVG and executable files are blocked.', 'error');
      return;
    }

    if (formData.partyId > 0) {
      setUploadingLogo(true);
      try {
        const res = await partyService.uploadPartyLogo(formData.partyId, file);
        const uploadedUrl = res?.data?.logoUrl || res?.logoUrl;
        setFormData((prev) => ({
          ...prev,
          coBrandingLogoUrl: uploadedUrl,
          enableCoBranding: true
        }));
        triggerToast('Clinic logo uploaded and verified successfully.', 'success');
      } catch (err) {
        console.error('Failed to upload logo:', err);
        triggerToast(err.response?.data?.message || 'Failed to upload clinic logo.', 'error');
      } finally {
        setUploadingLogo(false);
      }
    } else {
      const reader = new FileReader();
      reader.onload = () => {
        setFormData((prev) => ({
          ...prev,
          coBrandingLogoUrl: reader.result,
          enableCoBranding: true
        }));
      };
      reader.readAsDataURL(file);
      triggerToast('Logo selected for preview. It will be uploaded on party save.', 'info');
    }
  };

  // Financial Ledger (Khata) Handler
  const handleOpenLedgerModal = async (party) => {
    setLedgerModalTarget(party);
    setLedgerLoading(true);
    setLedgerData(null);
    try {
      const res = await partyService.getPartyLedger(party.partyId, {
        fromDate: ledgerFromDate,
        toDate: ledgerToDate
      });
      if (res && res.success) {
        setLedgerData(res.data);
      } else {
        triggerToast('Could not load ledger statement', 'warning');
      }
    } catch (err) {
      console.error('Error fetching party ledger:', err);
      triggerToast(err.response?.data?.message || 'Error fetching client ledger', 'error');
    } finally {
      setLedgerLoading(false);
    }
  };

  const handleFilterLedger = async () => {
    if (!ledgerModalTarget) return;
    setLedgerLoading(true);
    try {
      const res = await partyService.getPartyLedger(ledgerModalTarget.partyId, {
        fromDate: ledgerFromDate,
        toDate: ledgerToDate
      });
      if (res && res.success) {
        setLedgerData(res.data);
      }
    } catch (err) {
      triggerToast('Error refreshing ledger statement', 'error');
    } finally {
      setLedgerLoading(false);
    }
  };

  // Advance Deposit / Wallet Recharge Handlers
  const handleOpenAdvanceModal = (party) => {
    setAdvanceModalTarget(party);
    setAdvanceAmount(20000);
    setAdvanceMode('UPI');
    setAdvanceRef('');
    setAdvanceBank('');
    setAdvanceNotes('Advance deposit for test quota recharge');
  };

  const handleCreateAdvance = async (e) => {
    e.preventDefault();
    if (!advanceModalTarget) return;
    const numAmount = parseFloat(advanceAmount);
    if (isNaN(numAmount) || numAmount <= 0) {
      triggerToast('Please enter a valid deposit amount greater than ₹0', 'warning');
      return;
    }

    setSavingAdvance(true);
    try {
      const payload = {
        partyId: advanceModalTarget.partyId,
        amount: numAmount,
        paymentMode: advanceMode,
        transactionReference: advanceRef.trim() || undefined,
        bankName: advanceBank.trim() || undefined,
        notes: advanceNotes.trim() || undefined
      };
      const res = await b2bService.createAdvance(payload);
      const receiptNo = res?.receiptNumber || res?.ReceiptNumber || 'ADVANCE';
      triggerToast(`Advance ${receiptNo} for ₹${numAmount.toLocaleString('en-IN')} credited to ${advanceModalTarget.partyName}!`, 'success');
      setAdvanceModalTarget(null);

      // Refresh Ledger if currently viewed
      if (ledgerModalTarget && (ledgerModalTarget.partyId === advanceModalTarget.partyId)) {
        try {
          const lRes = await partyService.getPartyLedger(ledgerModalTarget.partyId, {
            fromDate: ledgerFromDate,
            toDate: ledgerToDate
          });
          if (lRes && lRes.success) setLedgerData(lRes.data);
        } catch (_) { }
      }

      loadParties();
    } catch (err) {
      console.error('Error creating advance:', err);
      triggerToast(err.response?.data?.message || err.message || 'Failed to record advance deposit', 'error');
    } finally {
      setSavingAdvance(false);
    }
  };

  // Status Toggle
  const handleToggleStatus = (party) => {
    setConfirmTarget({
      partyId: party.partyId,
      partyName: party.partyName,
      nextStatus: !party.isActive
    });
  };

  const confirmStatusChange = async () => {
    if (!confirmTarget) return;
    try {
      await partyService.updatePartyStatus(confirmTarget.partyId, confirmTarget.nextStatus);
      triggerToast(`Party ${confirmTarget.nextStatus ? 'activated' : 'deactivated'} successfully.`, 'success');
      setConfirmTarget(null);
      loadParties();
    } catch (err) {
      triggerToast(err.response?.data?.message || 'Failed to update status', 'error');
    }
  };

  // Field Validation Helpers (Non-restrictive: only core identifiers are mandatory; optional fields validated ONLY if provided)
  const validateField = (name, value, currentData = formData) => {
    let error = null;
    const strVal = typeof value === 'string' ? value.trim() : (value !== null && value !== undefined ? String(value).trim() : '');

    switch (name) {
      // ── MANDATORY FIELDS ──
      case 'partyCode':
        if (!strVal) {
          error = 'Party code is required';
        } else if (strVal.length < 2) {
          error = 'Party code must be at least 2 characters';
        } else if (!/^[A-Za-z0-9_\-\.]+$/.test(strVal)) {
          error = 'Only letters, numbers, hyphens, and underscores allowed';
        }
        break;

      case 'partyName':
        if (!strVal) {
          error = 'Client / Business Name is required';
        } else if (strVal.length < 2) {
          error = 'Client name must be at least 2 characters';
        }
        break;

      case 'classificationId':
        if (!value) {
          error = 'Please select a classification';
        }
        break;

      // ── CONDITIONAL MANDATORY (Portal Access) ──
      case 'portalUsername':
        if (currentData.enablePortalAccess) {
          if (!strVal) {
            error = 'Portal username is required when portal is enabled';
          } else if (strVal.length < 3) {
            error = 'Username must be at least 3 characters';
          } else if (!/^[A-Za-z0-9_\-\.]+$/.test(strVal)) {
            error = 'Only letters, numbers, hyphens, and underscores allowed';
          }
        }
        break;

      case 'portalPassword':
        if (currentData.enablePortalAccess) {
          if (!isEditMode && !strVal) {
            error = 'An initial portal password is required';
          } else if (strVal && strVal.length < 6) {
            error = 'Password must be at least 6 characters';
          }
        }
        break;

      // ── OPTIONAL FIELDS WITH FORMAT VALIDATION (Only validated if entered, NEVER mandatory) ──
      case 'contactMobile':
        if (strVal) {
          if (!/^[6-9]\d{9}$/.test(strVal)) {
            error = 'Enter a valid 10-digit mobile number starting with 6-9';
          }
        }
        break;

      case 'contactEmail':
        if (strVal) {
          if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(strVal)) {
            error = 'Please enter a valid email address (e.g. name@domain.com)';
          }
        }
        break;

      case 'gstNumber':
        if (strVal) {
          if (!/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/.test(strVal.toUpperCase())) {
            error = 'Invalid GSTIN format (15 characters, e.g. 27AAAAA0000A1Z5)';
          }
        }
        break;

      case 'panNumber':
        if (strVal) {
          if (!/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(strVal.toUpperCase())) {
            error = 'Invalid PAN format (10 characters, e.g. ABCDE1234F)';
          }
        }
        break;

      case 'creditLimit':
        if (strVal !== '') {
          const num = Number(strVal);
          if (isNaN(num) || num < 0) {
            error = 'Credit limit cannot be negative';
          }
        }
        break;

      case 'paymentTermsDays':
        if (strVal !== '') {
          const num = Number(strVal);
          if (isNaN(num) || num < 0 || num > 365) {
            error = 'Payment terms must be between 0 and 365 days';
          }
        }
        break;

      case 'gracePeriodDays':
        if (strVal !== '') {
          const num = Number(strVal);
          if (isNaN(num) || num < 0 || num > 90) {
            error = 'Grace period must be between 0 and 90 days';
          }
        }
        break;

      default:
        break;
    }

    return error;
  };

  const handleFieldChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));

    // If this field currently has an error, validate live and clear/update it
    if (errors[name]) {
      const err = validateField(name, value, { ...formData, [name]: value });
      setErrors(prev => {
        const next = { ...prev };
        if (err) {
          next[name] = err;
        } else {
          delete next[name];
        }
        return next;
      });
    }
  };

  const handleFieldBlur = (name) => {
    const err = validateField(name, formData[name], formData);
    setErrors(prev => {
      const next = { ...prev };
      if (err) {
        next[name] = err;
      } else {
        delete next[name];
      }
      return next;
    });
  };

  // Comprehensive Form Validation
  const validateForm = () => {
    const errs = {};
    const fieldsToValidate = [
      'partyCode',
      'partyName',
      'classificationId',
      'contactMobile',
      'contactEmail',
      'gstNumber',
      'panNumber',
      'creditLimit',
      'paymentTermsDays',
      'gracePeriodDays'
    ];

    if (formData.enablePortalAccess) {
      fieldsToValidate.push('portalUsername');
      fieldsToValidate.push('portalPassword');
    }

    fieldsToValidate.forEach((field) => {
      const err = validateField(field, formData[field], formData);
      if (err) {
        errs[field] = err;
      }
    });

    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  // Save Party
  const handleSave = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      triggerToast('Please correct the highlighted fields.', 'warning');
      setTimeout(() => {
        const firstErrorEl = document.querySelector('.party-field-input.has-error');
        if (firstErrorEl) {
          firstErrorEl.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
          firstErrorEl.focus?.();
        }
      }, 50);
      return;
    }

    setSaving(true);
    try {
      const payload = {
        partyCode: formData.partyCode.trim().toUpperCase(),
        partyName: formData.partyName.trim(),
        classificationId: parseInt(formData.classificationId, 10),
        defaultPanelId: formData.defaultPanelId ? parseInt(formData.defaultPanelId, 10) : null,
        contactPerson: formData.contactPerson?.trim() || null,
        contactMobile: formData.contactMobile?.trim() || null,
        contactEmail: formData.contactEmail?.trim() || null,
        address: formData.address?.trim() || null,
        city: formData.city?.trim() || null,
        state: formData.state?.trim() || null,
        gstNumber: formData.gstNumber?.trim() || null,
        panNumber: formData.panNumber?.trim() || null,
        creditLimit: parseFloat(formData.creditLimit) || 0.00,
        paymentTermsDays: parseInt(formData.paymentTermsDays, 10) || 0,
        creditPolicy: formData.creditPolicy || 'HYBRID',
        breachAction: formData.breachAction || 'BLOCK_REGISTRATION',
        gracePeriodDays: parseInt(formData.gracePeriodDays, 10) || 7,
        allowPortalRegistration: formData.allowPortalRegistration !== false,
        // Portal fields
        enablePortalAccess: formData.enablePortalAccess,
        portalUsername: formData.enablePortalAccess ? formData.portalUsername.trim() : null,
        portalPassword: formData.enablePortalAccess && formData.portalPassword ? formData.portalPassword : null,
        portalRole: formData.enablePortalAccess ? formData.portalRole : 'ADMIN',
        // Co-Branded Reports Settings
        enableCoBranding: Boolean(formData.enableCoBranding),
        coBrandingLogoUrl: formData.coBrandingLogoUrl?.trim() || null,
        coBrandingTagline: formData.coBrandingTagline?.trim() || null,
        coBrandingAddress: formData.coBrandingAddress?.trim() || null,
        coBrandingContact: formData.coBrandingContact?.trim() || null,
        coBrandingHeaderType: formData.coBrandingHeaderType || 'CO_BRANDED'
      };

      if (isEditMode) {
        await partyService.updateParty(formData.partyId, {
          ...payload,
          isActive: formData.isActive
        });
        triggerToast('B2B Client Party and Portal credentials updated successfully.', 'success');
      } else {
        await partyService.createParty(payload);
        triggerToast('B2B Client Party created with Portal Access credentials.', 'success');
      }

      setViewMode('list');
      loadParties();
    } catch (err) {
      console.error('Error saving party:', err);
      triggerToast(err.response?.data?.message || 'Error saving party', 'error');
    } finally {
      setSaving(false);
    }
  };

  // Open Quick Credential Modal from Table Action
  const handleOpenCredModal = (party) => {
    setCredModalTarget(party);
    setModalUsername(party.portalUsername || party.partyCode?.toLowerCase() || '');
    setModalPassword('');
    setModalRole(party.portalRole || 'ADMIN');
    setModalShowPassword(false);
  };

  const handleSaveModalCreds = async () => {
    if (!credModalTarget) return;
    const cleanUser = modalUsername.trim();
    const cleanPass = modalPassword.trim();

    if (!cleanUser) {
      triggerToast('Portal username is required.', 'warning');
      return;
    }
    if (cleanUser.length < 3) {
      triggerToast('Portal username must be at least 3 characters.', 'warning');
      return;
    }
    if (!/^[A-Za-z0-9_\-\.]+$/.test(cleanUser)) {
      triggerToast('Portal username can only contain letters, numbers, hyphens, and underscores.', 'warning');
      return;
    }
    if (!cleanPass && !credModalTarget.hasPortalAccess) {
      triggerToast('Password is required for new portal account.', 'warning');
      return;
    }
    if (cleanPass && cleanPass.length < 6) {
      triggerToast('Password must be at least 6 characters.', 'warning');
      return;
    }

    setSavingCreds(true);
    try {
      await partyService.provisionPortalUser(credModalTarget.partyId, {
        username: cleanUser,
        password: cleanPass || null,
        role: modalRole,
        fullName: credModalTarget.contactPerson || credModalTarget.partyName,
        email: credModalTarget.contactEmail || `${cleanUser}@client.lims`,
        mobile: credModalTarget.contactMobile,
        isActive: true
      });
      triggerToast(`Portal login credentials updated for ${credModalTarget.partyName}! Client can log in at /b2b/login.`, 'success');
      setCredModalTarget(null);
      loadParties();
    } catch (err) {
      triggerToast(err.response?.data?.message || 'Failed to update portal credentials', 'error');
    } finally {
      setSavingCreds(false);
    }
  };

  // Table Columns
  const columns = [
    {
      key: 'partyCode',
      header: 'Party Code',
      render: (row) => <strong style={{ color: '#2563eb', fontFamily: 'monospace', fontSize: '0.9rem' }}>{row.partyCode}</strong>
    },
    {
      key: 'partyName',
      header: 'Business Client / Party Name',
      render: (row) => (
        <div>
          <div style={{ fontWeight: 600, color: '#0f172a' }}>{row.partyName}</div>
          {row.contactPerson && (
            <div style={{ fontSize: '0.78rem', color: '#64748b' }}>
              👤 {row.contactPerson} {row.contactMobile ? `(${row.contactMobile})` : ''}
            </div>
          )}
        </div>
      )
    },
    {
      key: 'classificationName',
      header: 'Classification',
      render: (row) => (
        <span style={{
          background: '#f1f5f9',
          color: '#334155',
          padding: '0.2rem 0.6rem',
          borderRadius: '4px',
          fontSize: '0.75rem',
          fontWeight: 600
        }}>
          {row.classificationName}
        </span>
      )
    },
    {
      key: 'creditPolicy',
      header: 'Credit Policy & Terms',
      render: (row) => {
        const policy = row.creditPolicy || 'HYBRID';
        const limit = parseFloat(row.creditLimit || 0);
        const days = row.paymentTermsDays || 0;
        return (
          <div>
            {policy === 'HYBRID' && (
              <span className="badge-policy hybrid">⚡ Hybrid • ₹{limit.toLocaleString('en-IN')} / {days}d</span>
            )}
            {policy === 'PREPAID' && (
              <span className="badge-policy prepaid">💳 Prepaid Advance Only</span>
            )}
            {policy === 'LIMIT_ONLY' && (
              <span className="badge-policy limit">💰 Limit: ₹{limit.toLocaleString('en-IN')}</span>
            )}
            {policy === 'DAYS_ONLY' && (
              <span className="badge-policy days">📅 {days} Days Credit Term</span>
            )}
            <div style={{ fontSize: '0.72rem', color: '#64748b', marginTop: '2px' }}>
              Breach: <span style={{ fontWeight: 600, color: '#334155' }}>{(row.breachAction || 'BLOCK_REGISTRATION').replace('BLOCK_', 'HOLD ')}</span>
            </div>
          </div>
        );
      }
    },
    {
      key: 'totalExposure',
      header: 'Live Exposure / Headroom',
      render: (row) => {
        const exposure = parseFloat(row.totalExposure || 0);
        const available = parseFloat(row.availableCredit || 0);
        return (
          <div>
            <div style={{ fontWeight: 600, color: exposure > 0 ? '#b45309' : '#0f172a' }}>
              Exposure: ₹{exposure.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
            </div>
            <div style={{ fontSize: '0.75rem', color: available > 0 ? '#16a34a' : '#dc2626', fontWeight: 600 }}>
              Headroom: ₹{available.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
            </div>
          </div>
        );
      }
    },
    {
      key: 'portalAccess',
      header: 'Client Portal Login',
      render: (row) => (
        row.hasPortalAccess ? (
          <span className="badge-portal-active" title={`Username: ${row.portalUsername} (${row.portalRole})`}>
            🔑 {row.portalUsername} <span style={{ opacity: 0.7 }}>({row.portalRole})</span>
          </span>
        ) : (
          <span className="badge-portal-none">Not Configured</span>
        )
      )
    },
    {
      key: 'isActive',
      header: 'Status',
      render: (row) => <StatusBadge isActive={row.isActive} />
    },
    {
      key: 'actions',
      header: 'Actions',
      render: (row) => (
        <ActionMenu
          actions={[
            {
              label: '💳 Recharge Wallet / Add Advance',
              onClick: () => handleOpenAdvanceModal(row)
            },
            {
              label: '📖 View Financial Ledger (Khata)',
              onClick: () => handleOpenLedgerModal(row)
            },
            {
              label: '🔑 Manage Portal Login',
              onClick: () => handleOpenCredModal(row)
            },
            {
              label: '📋 View / Manage Contracts',
              onClick: () => navigate(`/masters/contracts?partyId=${row.partyId}`)
            },
            {
              label: '✏️ Edit Party Details',
              onClick: () => handleEdit(row)
            },
            {
              label: row.isActive ? 'Deactivate' : 'Activate',
              onClick: () => handleToggleStatus(row),
              danger: row.isActive
            }
          ]}
        />
      )
    }
  ];

  return (
    <MasterPageWrapper
      masterTitle="B2B Client & Party Master"
      masterName="Party"
      viewMode={viewMode}
      onToggleView={(mode) => {
        if (mode === 'form') handleAddNew();
        else setViewMode('list');
      }}
    >
      {viewMode === 'list' ? (
        <div className="party-master-container">
          <MasterToolbar
            searchPlaceholder="Search by Party Name, Code, Contact..."
            searchValue={searchQuery}
            onSearchChange={setSearchQuery}
            filters={[
              {
                id: 'classFilter',
                label: 'Classification',
                value: classFilter,
                onChange: setClassFilter,
                options: [
                  { value: '', label: 'All Classifications' },
                  ...classifications.map(c => ({ value: String(c.classificationId), label: c.name }))
                ]
              },
              {
                id: 'statusFilter',
                label: 'Status',
                value: statusFilter,
                onChange: setStatusFilter,
                options: [
                  { value: 'all', label: 'All Statuses' },
                  { value: 'active', label: 'Active Only' },
                  { value: 'inactive', label: 'Inactive Only' }
                ]
              }
            ]}
          />

          <MasterTable
            columns={columns}
            data={pagedParties}
            loading={loadingParties}
            emptyTitle="No B2B Parties Found"
            emptyDescription="Register your corporate clients, hospital tie-ups, clinics, and collection centers."
            pagination={partyPaginationProps}
          />
        </div>
      ) : (
        <form onSubmit={handleSave} className="party-master-container">
          {/* Card 1: Business Identification */}
          <div className="party-form-card">
            <div className="party-section-header">
              <div className="party-section-title-wrap">
                <div className="party-section-icon blue">🏢</div>
                <div>
                  <h3 className="party-section-title">Business Identification</h3>
                  <p className="party-section-desc">Specify unique business code, legal name, and commercial classification.</p>
                </div>
              </div>
            </div>

            <div className="party-grid-3">
              <div className="party-field-group">
                <label className="party-field-label">Party Code <span className="req">*</span></label>
                <input
                  type="text"
                  className={`party-field-input ${errors.partyCode ? 'has-error' : ''}`}
                  placeholder="e.g. CORP_INFOSYS, HOSP_APOLLO"
                  value={formData.partyCode}
                  onChange={(e) => handleFieldChange('partyCode', e.target.value.toUpperCase())}
                  onBlur={() => handleFieldBlur('partyCode')}
                  disabled={isEditMode}
                  maxLength={50}
                />
                {errors.partyCode && <span className="party-field-error">⚠️ {errors.partyCode}</span>}
                <span className="party-field-hint">Unique institutional identifier</span>
              </div>

              <div className="party-field-group" style={{ gridColumn: 'span 2' }}>
                <label className="party-field-label">Client / Business Name <span className="req">*</span></label>
                <input
                  type="text"
                  className={`party-field-input ${errors.partyName ? 'has-error' : ''}`}
                  placeholder="e.g. Infosys Technologies Ltd (Health Division)"
                  value={formData.partyName}
                  onChange={(e) => handleFieldChange('partyName', e.target.value)}
                  onBlur={() => handleFieldBlur('partyName')}
                  maxLength={200}
                />
                {errors.partyName && <span className="party-field-error">⚠️ {errors.partyName}</span>}
              </div>
            </div>

            <div className="party-grid-2">
              <div className="party-field-group">
                <label className="party-field-label">Party Classification <span className="req">*</span></label>
                <select
                  className={`party-field-input ${errors.classificationId ? 'has-error' : ''}`}
                  value={formData.classificationId}
                  onChange={(e) => handleFieldChange('classificationId', e.target.value)}
                  onBlur={() => handleFieldBlur('classificationId')}
                >
                  <option value="">-- Select Classification --</option>
                  {classifications.map(c => (
                    <option key={c.classificationId} value={c.classificationId}>
                      {c.name}
                    </option>
                  ))}
                </select>
                {errors.classificationId && <span className="party-field-error">⚠️ {errors.classificationId}</span>}
              </div>

              <div className="party-field-group">
                <label className="party-field-label">Default Panel Tariff <span className="opt">(Optional)</span></label>
                <select
                  className="party-field-input"
                  value={formData.defaultPanelId}
                  onChange={(e) => handleFieldChange('defaultPanelId', e.target.value)}
                >
                  <option value="">-- Standard Cash / Walk-in Tariff --</option>
                  {panels.map(p => (
                    <option key={p.panelId} value={p.panelId}>
                      {p.panelName} ({p.panelCode})
                    </option>
                  ))}
                </select>
                <span className="party-field-hint">Fallback tariff if contract rate is not mapped</span>
              </div>
            </div>

            <div className="party-grid-2">
              <div className="party-field-group">
                <label className="party-field-label">GSTIN / Tax ID <span className="opt">(Optional)</span></label>
                <input
                  type="text"
                  className={`party-field-input ${errors.gstNumber ? 'has-error' : ''}`}
                  placeholder="e.g. 27AAAAA0000A1Z5"
                  value={formData.gstNumber}
                  onChange={(e) => handleFieldChange('gstNumber', e.target.value.toUpperCase().slice(0, 15))}
                  onBlur={() => handleFieldBlur('gstNumber')}
                  maxLength={15}
                />
                {errors.gstNumber && <span className="party-field-error">⚠️ {errors.gstNumber}</span>}
                <span className="party-field-hint">15-character GST format, validated only if entered</span>
              </div>

              <div className="party-field-group">
                <label className="party-field-label">PAN Number <span className="opt">(Optional)</span></label>
                <input
                  type="text"
                  className={`party-field-input ${errors.panNumber ? 'has-error' : ''}`}
                  placeholder="e.g. ABCDE1234F"
                  value={formData.panNumber}
                  onChange={(e) => handleFieldChange('panNumber', e.target.value.toUpperCase().slice(0, 10))}
                  onBlur={() => handleFieldBlur('panNumber')}
                  maxLength={10}
                />
                {errors.panNumber && <span className="party-field-error">⚠️ {errors.panNumber}</span>}
                <span className="party-field-hint">10-character PAN format, validated only if entered</span>
              </div>
            </div>
          </div>

          {/* Card 2: Commercial Credit & Risk Governance */}
          <div className="party-form-card">
            <div className="party-section-header">
              <div className="party-section-title-wrap">
                <div className="party-section-icon purple">🛡️</div>
                <div>
                  <h3 className="party-section-title">Commercial Credit &amp; Risk Governance</h3>
                  <p className="party-section-desc">Configure credit control model (limit vs days), grace periods, and automated breach enforcement.</p>
                </div>
              </div>
            </div>

            <div className="party-grid-3">
              <div className="party-field-group">
                <label className="party-field-label">Credit Control Policy <span className="req">*</span></label>
                <select
                  className="party-field-input"
                  value={formData.creditPolicy}
                  onChange={(e) => handleFieldChange('creditPolicy', e.target.value)}
                >
                  <option value="HYBRID">⚡ Both: Credit Limit + Credit Days (Standard)</option>
                  <option value="LIMIT_ONLY">💰 Credit Limit (₹) Ceiling Only</option>
                  <option value="DAYS_ONLY">📅 Credit Terms (Days) Period Only</option>
                  <option value="PREPAID">💳 Prepaid Deposit / Advance Wallet Only</option>
                </select>
                <span className="party-field-hint">Operational policy governing account exposure</span>
              </div>

              <div className="party-field-group">
                <label className="party-field-label">Approved Credit Limit (₹) <span className="opt">(Optional)</span></label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  className={`party-field-input ${errors.creditLimit ? 'has-error' : ''}`}
                  value={formData.creditLimit}
                  onChange={(e) => handleFieldChange('creditLimit', e.target.value)}
                  onBlur={() => handleFieldBlur('creditLimit')}
                  disabled={formData.creditPolicy === 'PREPAID' || formData.creditPolicy === 'DAYS_ONLY'}
                />
                {errors.creditLimit && <span className="party-field-error">⚠️ {errors.creditLimit}</span>}
                <span className="party-field-hint">
                  {formData.creditPolicy === 'PREPAID' ? 'Disabled: Requires advance deposit' : 'Maximum allowable unbilled balance'}
                </span>
              </div>

              <div className="party-field-group">
                <label className="party-field-label">Credit / Payment Terms (Days) <span className="opt">(Optional)</span></label>
                <input
                  type="number"
                  min="0"
                  max="365"
                  className={`party-field-input ${errors.paymentTermsDays ? 'has-error' : ''}`}
                  value={formData.paymentTermsDays}
                  onChange={(e) => handleFieldChange('paymentTermsDays', e.target.value)}
                  onBlur={() => handleFieldBlur('paymentTermsDays')}
                  disabled={formData.creditPolicy === 'PREPAID' || formData.creditPolicy === 'LIMIT_ONLY'}
                />
                {errors.paymentTermsDays && <span className="party-field-error">⚠️ {errors.paymentTermsDays}</span>}
                <span className="party-field-hint">
                  {formData.creditPolicy === 'PREPAID' ? 'Disabled: Cash / Advance only' : 'Credit period from invoice date'}
                </span>
              </div>
            </div>

            <div className="party-grid-2">
              <div className="party-field-group">
                <label className="party-field-label">Automated Action on Breach / Expiry <span className="req">*</span></label>
                <select
                  className="party-field-input"
                  value={formData.breachAction}
                  onChange={(e) => handleFieldChange('breachAction', e.target.value)}
                >
                  <option value="BLOCK_REGISTRATION">🚫 Block New Patient Booking &amp; Sample Collection</option>
                  <option value="BLOCK_REPORTS">📑 Block Final Signed Report Release &amp; PDF Download</option>
                  <option value="BLOCK_ALL">🔒 Strict Hard Lock: Block Both Booking &amp; Reports</option>
                  <option value="WARN_ONLY">⚠️ Soft Warning Only: Flash Alert &amp; Email Notice</option>
                </select>
                <span className="party-field-hint">Automated enforcement action triggered when credit terms are breached</span>
              </div>

              <div className="party-field-group">
                <label className="party-field-label">Grace Period (Days) <span className="opt">(Optional)</span></label>
                <input
                  type="number"
                  min="0"
                  max="90"
                  className={`party-field-input ${errors.gracePeriodDays ? 'has-error' : ''}`}
                  value={formData.gracePeriodDays}
                  onChange={(e) => handleFieldChange('gracePeriodDays', e.target.value)}
                  onBlur={() => handleFieldBlur('gracePeriodDays')}
                />
                {errors.gracePeriodDays && <span className="party-field-error">⚠️ {errors.gracePeriodDays}</span>}
                <span className="party-field-hint">Additional grace window before strict enforcement applies</span>
              </div>
            </div>

            {/* Dynamic Governance Explainer */}
            <div style={{ marginTop: '1rem', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '12px 16px', fontSize: '0.82rem', color: '#475569', lineHeight: 1.5 }}>
              <strong style={{ color: '#0f172a' }}>Multi-Center Governance Rule: </strong>
              {formData.creditPolicy === 'HYBRID' && 'Account will trigger automated hold if either total exposure exceeds ₹' + parseFloat(formData.creditLimit || 0).toLocaleString('en-IN') + ' OR invoices remain unpaid beyond ' + (parseInt(formData.paymentTermsDays, 10) || 0) + ' days.'}
              {formData.creditPolicy === 'LIMIT_ONLY' && 'Account will trigger automated hold strictly when total exposure crosses ₹' + parseFloat(formData.creditLimit || 0).toLocaleString('en-IN') + ' (amount-capped).'}
              {formData.creditPolicy === 'DAYS_ONLY' && 'Account allows unlimited volume within ' + (parseInt(formData.paymentTermsDays, 10) || 0) + ' days, but triggers hold if any invoice ages beyond term + grace window.'}
              {formData.creditPolicy === 'PREPAID' && 'Zero credit extended. Patient tests are only accepted if unapplied advance deposit balance covers the test booking amount.'}
            </div>
          </div>

          {/* Card 2: Contact & Location */}
          <div className="party-form-card">
            <div className="party-section-header">
              <div className="party-section-title-wrap">
                <div className="party-section-icon amber">📍</div>
                <div>
                  <h3 className="party-section-title">Contact &amp; Location</h3>
                  <p className="party-section-desc">Provide primary representative details and official billing address.</p>
                </div>
              </div>
            </div>

            <div className="party-grid-3">
              <div className="party-field-group">
                <label className="party-field-label">Contact Person <span className="opt">(Optional)</span></label>
                <input
                  type="text"
                  className="party-field-input"
                  placeholder="e.g. Dr. Rajesh Kulkarni"
                  value={formData.contactPerson}
                  onChange={(e) => handleFieldChange('contactPerson', e.target.value)}
                  maxLength={100}
                />
              </div>

              <div className="party-field-group">
                <label className="party-field-label">Phone / Mobile <span className="opt">(Optional)</span></label>
                <input
                  type="tel"
                  className={`party-field-input ${errors.contactMobile ? 'has-error' : ''}`}
                  placeholder="e.g. 9876543210 (10 digits)"
                  value={formData.contactMobile}
                  onChange={(e) => {
                    const digits = e.target.value.replace(/\D/g, '').slice(0, 10);
                    handleFieldChange('contactMobile', digits);
                  }}
                  onBlur={() => handleFieldBlur('contactMobile')}
                  maxLength={10}
                />
                {errors.contactMobile && <span className="party-field-error">⚠️ {errors.contactMobile}</span>}
                <span className="party-field-hint">Validated only if entered (10-digit number)</span>
              </div>

              <div className="party-field-group">
                <label className="party-field-label">Email Address <span className="opt">(Optional)</span></label>
                <input
                  type="email"
                  className={`party-field-input ${errors.contactEmail ? 'has-error' : ''}`}
                  placeholder="e.g. billing@partnerhospital.com"
                  value={formData.contactEmail}
                  onChange={(e) => handleFieldChange('contactEmail', e.target.value)}
                  onBlur={() => handleFieldBlur('contactEmail')}
                  maxLength={100}
                />
                {errors.contactEmail && <span className="party-field-error">⚠️ {errors.contactEmail}</span>}
                <span className="party-field-hint">Validated only if entered (standard email format)</span>
              </div>
            </div>

            <div className="party-grid-3">
              <div className="party-field-group">
                <label className="party-field-label">Address Line <span className="opt">(Optional)</span></label>
                <input
                  type="text"
                  className="party-field-input"
                  placeholder="Premises, Street, Building"
                  value={formData.address}
                  onChange={(e) => handleFieldChange('address', e.target.value)}
                  maxLength={300}
                />
              </div>

              <div className="party-field-group">
                <label className="party-field-label">City <span className="opt">(Optional)</span></label>
                <input
                  type="text"
                  className="party-field-input"
                  placeholder="e.g. Aurangabad"
                  value={formData.city}
                  onChange={(e) => handleFieldChange('city', e.target.value)}
                  maxLength={100}
                />
              </div>

              <div className="party-field-group">
                <label className="party-field-label">State <span className="opt">(Optional)</span></label>
                <input
                  type="text"
                  className="party-field-input"
                  placeholder="e.g. Maharashtra"
                  value={formData.state}
                  onChange={(e) => handleFieldChange('state', e.target.value)}
                  maxLength={100}
                />
              </div>
            </div>
          </div>

          {/* Card 3: B2B Client Portal Login Credentials (KEY FEATURE REQUESTED) */}
          <div className="party-form-card" style={{ borderColor: '#bfdbfe', background: '#f8fafc' }}>
            <div className="party-section-header">
              <div className="party-section-title-wrap">
                <div className="party-section-icon purple">🔐</div>
                <div>
                  <h3 className="party-section-title">Client Partner Portal Access &amp; Login Credentials</h3>
                  <p className="party-section-desc">
                    Configure institutional username and password so this client can securely sign in at <strong style={{ color: '#2563eb' }}>/b2b/login</strong>.
                  </p>
                </div>
              </div>
              <span className="badge-portal-active" style={{ fontSize: '0.8rem', padding: '0.3rem 0.75rem' }}>
                Self-Service Portal
              </span>
            </div>

            <div className={`party-portal-box ${formData.enablePortalAccess ? 'active' : ''}`}>
              <div
                className="party-portal-toggle-header"
                onClick={() => setFormData(prev => ({
                  ...prev,
                  enablePortalAccess: !prev.enablePortalAccess,
                  portalUsername: !prev.enablePortalAccess && !prev.portalUsername
                    ? (prev.partyCode ? prev.partyCode.toLowerCase() : '')
                    : prev.portalUsername
                }))}
              >
                <div className="party-portal-toggle-left">
                  <input
                    type="checkbox"
                    className="party-portal-checkbox"
                    checked={formData.enablePortalAccess}
                    onChange={(e) => setFormData({
                      ...formData,
                      enablePortalAccess: e.target.checked,
                      portalUsername: e.target.checked && !formData.portalUsername
                        ? (formData.partyCode ? formData.partyCode.toLowerCase() : '')
                        : formData.portalUsername
                    })}
                  />
                  <div>
                    <h4 className="party-portal-title">Enable B2B Client Portal Login</h4>
                    <p className="party-portal-subtitle">
                      Allows authorized corporate / hospital staff to view real-time patient reports, download corporate invoices, and view ledger balance.
                    </p>
                  </div>
                </div>
              </div>

              {formData.enablePortalAccess && (
                <div style={{ marginTop: '1.25rem' }}>
                  <div className="party-portal-notice">
                    <span className="party-portal-notice-icon">ℹ️</span>
                    <div>
                      <strong>Portal URL for Client:</strong> The representative will use URL <code>/b2b/login</code> with the username and password below.
                      Tenant isolation is strictly enforced — this client will NEVER see another corporate partner's orders or financial records.
                    </div>
                  </div>

                  <div className="party-grid-3">
                    <div className="party-field-group">
                      <label className="party-field-label">Portal Username <span className="req">*</span></label>
                      <input
                        type="text"
                        className={`party-field-input ${errors.portalUsername ? 'has-error' : ''}`}
                        placeholder="e.g. techcorp_admin, apollo_lab"
                        value={formData.portalUsername}
                        onChange={(e) => handleFieldChange('portalUsername', e.target.value.toLowerCase().replace(/[^a-z0-9_\-\.]/g, ''))}
                        onBlur={() => handleFieldBlur('portalUsername')}
                        maxLength={50}
                      />
                      {errors.portalUsername && <span className="party-field-error">⚠️ {errors.portalUsername}</span>}
                      <span className="party-field-hint">Unique username for client portal login</span>
                    </div>

                    <div className="party-field-group">
                      <label className="party-field-label">
                        {isEditMode ? 'Reset Portal Password' : 'Initial Portal Password'}{' '}
                        {!isEditMode ? <span className="req">*</span> : <span className="opt">(Optional)</span>}
                      </label>
                      <div className="party-password-wrap">
                        <input
                          type={showPortalPassword ? 'text' : 'password'}
                          className={`party-field-input ${errors.portalPassword ? 'has-error' : ''}`}
                          placeholder={isEditMode ? 'Leave blank to keep existing password' : 'Min 6 characters, e.g. ClientPass123!'}
                          value={formData.portalPassword}
                          onChange={(e) => handleFieldChange('portalPassword', e.target.value)}
                          onBlur={() => handleFieldBlur('portalPassword')}
                          maxLength={50}
                        />
                        <button
                          type="button"
                          className="party-password-toggle"
                          onClick={() => setShowPortalPassword(!showPortalPassword)}
                          title={showPortalPassword ? 'Hide password' : 'Show password'}
                        >
                          {showPortalPassword ? '👁️' : '🔒'}
                        </button>
                      </div>
                      {errors.portalPassword && <span className="party-field-error">⚠️ {errors.portalPassword}</span>}
                      <span className="party-field-hint">
                        {isEditMode ? 'Optional: Only enter if you wish to reset their password' : 'Secure password given to client administrator'}
                      </span>
                    </div>

                    <div className="party-field-group">
                      <label className="party-field-label">Portal Authority Role</label>
                      <select
                        className="party-field-input"
                        value={formData.portalRole}
                        onChange={(e) => handleFieldChange('portalRole', e.target.value)}
                      >
                        <option value="ADMIN">ADMIN — Full Access (Orders, Reports, Invoices, Ledger &amp; Booking)</option>
                        <option value="BOOKING">BOOKING — Registration &amp; Clinical (Book Orders, Scoped Search &amp; View)</option>
                        <option value="BILLING">BILLING — Financial Access (Invoices &amp; Statements Only)</option>
                        <option value="VIEWER">VIEWER — Clinical Access (Orders &amp; Reports Only, No Booking)</option>
                      </select>
                      <span className="party-field-hint">Granular role-based scope within the customer portal</span>
                    </div>
                  </div>

                  <div className="party-portal-toggle-left" style={{ marginTop: '1.25rem', paddingTop: '1rem', borderTop: '1px dashed #cbd5e1' }}>
                    <input
                      type="checkbox"
                      id="allowPortalRegistrationCheck"
                      className="party-portal-checkbox"
                      checked={formData.allowPortalRegistration !== false}
                      onChange={(e) => setFormData({
                        ...formData,
                        allowPortalRegistration: e.target.checked
                      })}
                    />
                    <div>
                      <label htmlFor="allowPortalRegistrationCheck" className="party-portal-title" style={{ cursor: 'pointer' }}>
                        Allow Client Patient Self-Registration (Strictly Scoped)
                      </label>
                      <p className="party-portal-subtitle">
                        When enabled, this client can directly book patients via their portal. They will strictly only see past patients previously registered under their facility. If disabled, self-registration is locked down.
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Card 5: Co-Branded Reports & Logo Integration */}
          <div className="party-card party-portal-card" style={{ marginTop: '1.5rem', borderLeft: '4px solid #0284c7' }}>
            <div className="party-card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <h3 className="party-card-title">🎨 Co-Branded Reports &amp; Clinic Header</h3>
                <p className="party-card-subtitle">
                  Print the client's clinic name, logo, and address alongside the laboratory certification on patient reports.
                </p>
              </div>
            </div>

            <div className="party-card-body">
              <div className="party-portal-toggle-left">
                <input
                  type="checkbox"
                  id="enableCoBrandingCheck"
                  className="party-portal-checkbox"
                  checked={formData.enableCoBranding}
                  onChange={(e) => setFormData({
                    ...formData,
                    enableCoBranding: e.target.checked
                  })}
                />
                <div>
                  <label htmlFor="enableCoBrandingCheck" className="party-portal-title" style={{ cursor: 'pointer' }}>
                    Enable Co-Branding Header on Patient Reports
                  </label>
                  <p className="party-portal-subtitle">
                    When active, pathology and radiology reports generated for this party's patients will render their clinic logo, address, and tagline prominently as the Referring / Collection Centre partner.
                  </p>
                </div>
              </div>

              {formData.enableCoBranding && (
                <div style={{ marginTop: '1.25rem', paddingTop: '1.25rem', borderTop: '1px solid #e2e8f0' }}>
                  <div className="party-form-grid">
                    {/* Logo Upload & Preview */}
                    <div className="party-field-group" style={{ gridColumn: 'span 2' }}>
                      <label className="party-field-label">Clinic Logo (PNG, JPG, WebP — Max 2MB) <span className="opt">(Optional)</span></label>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '1.5rem', marginTop: '0.5rem' }}>
                        {formData.coBrandingLogoUrl ? (
                          <div style={{ position: 'relative', border: '1px solid #cbd5e1', borderRadius: '8px', padding: '6px', background: '#f8fafc' }}>
                            <img
                              src={formData.coBrandingLogoUrl}
                              alt="Clinic Logo Preview"
                              style={{ maxHeight: '60px', maxWidth: '180px', objectFit: 'contain', display: 'block' }}
                            />
                            <button
                              type="button"
                              onClick={() => handleFieldChange('coBrandingLogoUrl', '')}
                              style={{
                                position: 'absolute', top: '-8px', right: '-8px',
                                background: '#ef4444', color: '#fff', border: 'none',
                                borderRadius: '50%', width: '20px', height: '20px',
                                fontSize: '11px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center'
                              }}
                              title="Remove Logo"
                            >
                              ✕
                            </button>
                          </div>
                        ) : (
                          <div style={{ width: '120px', height: '60px', border: '2px dashed #cbd5e1', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94a3b8', fontSize: '12px' }}>
                            No Logo
                          </div>
                        )}

                        <div>
                          <input
                            type="file"
                            id="coBrandingLogoInput"
                            accept="image/png,image/jpeg,image/webp"
                            style={{ display: 'none' }}
                            onChange={handleLogoFileChange}
                          />
                          <button
                            type="button"
                            className="btn btn-secondary"
                            onClick={() => document.getElementById('coBrandingLogoInput')?.click()}
                            disabled={uploadingLogo}
                            style={{ fontSize: '0.85rem', padding: '0.5rem 1rem' }}
                          >
                            {uploadingLogo ? '⏳ Verifying & Uploading...' : formData.coBrandingLogoUrl ? '🔄 Change Logo' : '📤 Upload Clinic Logo'}
                          </button>
                          <span className="party-field-hint" style={{ display: 'block', marginTop: '4px' }}>
                            Optional: PNG / JPG up to 2MB. Reports will still generate if no logo is provided.
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="party-field-group">
                      <label className="party-field-label">Header Tagline / Subtitle <span className="opt">(Optional)</span></label>
                      <input
                        type="text"
                        className="party-field-input"
                        placeholder="e.g. Authorized Collection & Diagnostics Partner"
                        value={formData.coBrandingTagline}
                        onChange={(e) => handleFieldChange('coBrandingTagline', e.target.value)}
                        maxLength={200}
                      />
                      <span className="party-field-hint">Printed under the clinic name on reports</span>
                    </div>

                    <div className="party-field-group">
                      <label className="party-field-label">Header Contact Phone / Email <span className="opt">(Optional)</span></label>
                      <input
                        type="text"
                        className="party-field-input"
                        placeholder="e.g. +91 98765 43210 | clinic@health.com"
                        value={formData.coBrandingContact}
                        onChange={(e) => handleFieldChange('coBrandingContact', e.target.value)}
                        maxLength={100}
                      />
                    </div>

                    <div className="party-field-group" style={{ gridColumn: 'span 2' }}>
                      <label className="party-field-label">Clinic Print Address <span className="opt">(Optional)</span></label>
                      <input
                        type="text"
                        className="party-field-input"
                        placeholder="e.g. 104, Sunrise Avenue, Near Civil Hospital, MG Road"
                        value={formData.coBrandingAddress}
                        onChange={(e) => handleFieldChange('coBrandingAddress', e.target.value)}
                        maxLength={400}
                      />
                      <span className="party-field-hint">Overrides default address on the report banner</span>
                    </div>
                  </div>

                  {/* Live Report Header Visual Preview Box */}
                  <div style={{ marginTop: '1.25rem', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '1rem' }}>
                    <span style={{ fontSize: '11px', fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      👁️ Real-Time Report Header Preview:
                    </span>
                    <div style={{
                      marginTop: '8px',
                      background: '#ffffff',
                      border: '2px solid #e2e8f0',
                      borderRadius: '6px',
                      padding: '12px 16px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: '1rem'
                    }}>
                      {/* Left: Party Clinic */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        {formData.coBrandingLogoUrl ? (
                          <img src={formData.coBrandingLogoUrl} alt="Logo" style={{ maxHeight: '45px', maxWidth: '120px', objectFit: 'contain' }} />
                        ) : (
                          <div style={{ width: '40px', height: '40px', borderRadius: '6px', background: '#0284c7', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: '16px' }}>
                            {(formData.partyName || 'P').charAt(0)}
                          </div>
                        )}
                        <div>
                          <div style={{ fontWeight: 700, fontSize: '15px', color: '#0f172a' }}>{formData.partyName || 'Client Clinic Name'}</div>
                          <div style={{ fontSize: '11px', color: '#0284c7', fontWeight: 600 }}>{formData.coBrandingTagline || 'Referred By / Collection Partner'}</div>
                          <div style={{ fontSize: '10px', color: '#64748b' }}>{formData.coBrandingAddress || formData.address || 'Clinic Address Details'}</div>
                        </div>
                      </div>

                      {/* Middle: Badge */}
                      <div style={{ textAlign: 'center', padding: '0 10px', borderLeft: '1px dashed #cbd5e1', borderRight: '1px dashed #cbd5e1' }}>
                        <span style={{ fontSize: '9px', fontWeight: 700, background: '#f1f5f9', color: '#475569', padding: '2px 6px', borderRadius: '4px', textTransform: 'uppercase' }}>
                          In Collaboration With
                        </span>
                      </div>

                      {/* Right: Central Lab */}
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ fontWeight: 700, fontSize: '14px', color: '#1e3a8a' }}>CENTRAL REFERENCE LABORATORY</div>
                        <div style={{ fontSize: '10px', color: '#10b981', fontWeight: 600 }}>ISO 15189 • NABL ACCREDITED LAB</div>
                        <div style={{ fontSize: '10px', color: '#64748b' }}>Complete Clinical Diagnostic &amp; Pathology Services</div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Form Actions */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem', marginBottom: '2rem' }}>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => setViewMode('list')}
              disabled={saving}
              style={{ padding: '0.65rem 1.25rem', borderRadius: '8px' }}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={saving}
              style={{ padding: '0.65rem 1.5rem', borderRadius: '8px', fontWeight: 600 }}
            >
              {saving ? 'Saving Party...' : isEditMode ? 'Update B2B Client & Credentials' : 'Save B2B Client & Create Portal Login'}
            </button>
          </div>
        </form>
      )}

      {/* Quick Portal Credential Modal from Table Action */}
      {credModalTarget && (
        <div className="party-cred-modal-overlay" onClick={() => setCredModalTarget(null)}>
          <div className="party-cred-modal" onClick={(e) => e.stopPropagation()}>
            <div className="party-cred-modal-header">
              <h3 className="party-cred-modal-title">🔑 Manage Client Portal Login</h3>
              <button className="party-cred-modal-close" onClick={() => setCredModalTarget(null)}>×</button>
            </div>
            <div className="party-cred-modal-body">
              <div style={{ marginBottom: '1.25rem' }}>
                <div style={{ fontWeight: 700, fontSize: '1rem', color: '#0f172a' }}>{credModalTarget.partyName}</div>
                <div style={{ fontSize: '0.8rem', color: '#64748b' }}>Code: {credModalTarget.partyCode} | Portal Status: {credModalTarget.hasPortalAccess ? 'Active' : 'Unconfigured'}</div>
              </div>

              <div className="party-field-group" style={{ marginBottom: '1rem' }}>
                <label className="party-field-label">Portal Username <span className="req">*</span></label>
                <input
                  type="text"
                  className="party-field-input"
                  value={modalUsername}
                  onChange={(e) => setModalUsername(e.target.value.toLowerCase().trim())}
                  placeholder="e.g. client_admin"
                />
              </div>

              <div className="party-field-group" style={{ marginBottom: '1rem' }}>
                <label className="party-field-label">New / Reset Password</label>
                <div className="party-password-wrap">
                  <input
                    type={modalShowPassword ? 'text' : 'password'}
                    className="party-field-input"
                    value={modalPassword}
                    onChange={(e) => setModalPassword(e.target.value)}
                    placeholder="Enter new password (min 6 chars)"
                  />
                  <button
                    type="button"
                    className="party-password-toggle"
                    onClick={() => setModalShowPassword(!modalShowPassword)}
                  >
                    {modalShowPassword ? '👁️' : '🔒'}
                  </button>
                </div>
                <span className="party-field-hint">Enter a new password to reset client's portal access</span>
              </div>

              <div className="party-field-group">
                <label className="party-field-label">Portal Role</label>
                <select
                  className="party-field-input"
                  value={modalRole}
                  onChange={(e) => setModalRole(e.target.value)}
                >
                  <option value="ADMIN">ADMIN (Full Access)</option>
                  <option value="BILLING">BILLING (Invoices &amp; Ledger)</option>
                  <option value="VIEWER">VIEWER (Reports &amp; Orders)</option>
                </select>
              </div>
            </div>
            <div className="party-cred-modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setCredModalTarget(null)}
                disabled={savingCreds}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleSaveModalCreds}
                disabled={savingCreds}
              >
                {savingCreds ? 'Saving Credentials...' : 'Save Portal Credentials'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Financial Ledger (Khata) Modal */}
      {ledgerModalTarget && (
        <div className="party-cred-modal-overlay" onClick={() => setLedgerModalTarget(null)}>
          <div className="party-ledger-modal" onClick={(e) => e.stopPropagation()}>
            <div className="party-cred-modal-header">
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <span style={{ fontSize: '1.4rem' }}>📖</span>
                <div>
                  <h3 className="party-cred-modal-title">Financial Ledger &amp; Khata Statement</h3>
                  <div style={{ fontSize: '0.8rem', color: '#64748b' }}>
                    {ledgerModalTarget.partyName} ({ledgerModalTarget.partyCode}) &bull; Policy: <strong>{ledgerData?.creditPolicy || ledgerModalTarget.creditPolicy || 'HYBRID'}</strong>
                  </div>
                </div>
              </div>
              <button
                type="button"
                className="party-cred-modal-close"
                onClick={() => setLedgerModalTarget(null)}
              >
                &times;
              </button>
            </div>

            <div className="party-ledger-modal-body">
              {/* Financial KPI Cards */}
              <div className="ledger-kpi-grid">
                <div className="ledger-kpi-card">
                  <div className="ledger-kpi-title">Approved Credit Limit</div>
                  <div className="ledger-kpi-val">₹{parseFloat(ledgerData?.creditLimit || ledgerModalTarget.creditLimit || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</div>
                </div>
                <div className="ledger-kpi-card">
                  <div className="ledger-kpi-title">Total Invoiced (Debits)</div>
                  <div className="ledger-kpi-val danger">₹{parseFloat(ledgerData?.invoicedReceivables || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</div>
                </div>
                <div className="ledger-kpi-card">
                  <div className="ledger-kpi-title">Unapplied Advances / Paid</div>
                  <div className="ledger-kpi-val positive">₹{parseFloat(ledgerData?.unappliedAdvances || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</div>
                </div>
                <div className="ledger-kpi-card">
                  <div className="ledger-kpi-title">Total Net Exposure</div>
                  <div className="ledger-kpi-val warning">₹{parseFloat(ledgerData?.totalExposure || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</div>
                </div>
                <div className="ledger-kpi-card">
                  <div className="ledger-kpi-title">Available Booking Headroom</div>
                  <div className={`ledger-kpi-val ${(ledgerData?.availableCredit || 0) > 0 ? 'positive' : 'danger'}`}>
                    ₹{parseFloat(ledgerData?.availableCredit || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                  </div>
                </div>
              </div>

              {/* Date Filters */}
              <div className="ledger-filter-row">
                <div className="ledger-filter-item">
                  <label>Statement From</label>
                  <input
                    type="date"
                    value={ledgerFromDate}
                    onChange={(e) => setLedgerFromDate(e.target.value)}
                  />
                </div>
                <div className="ledger-filter-item">
                  <label>Statement To</label>
                  <input
                    type="date"
                    value={ledgerToDate}
                    onChange={(e) => setLedgerToDate(e.target.value)}
                  />
                </div>
                <button
                  type="button"
                  className="btn btn-secondary"
                  style={{ padding: '6px 14px', fontSize: '0.85rem' }}
                  onClick={handleFilterLedger}
                  disabled={ledgerLoading}
                >
                  {ledgerLoading ? 'Loading...' : 'Filter Date Range'}
                </button>
              </div>

              {/* Transactions Table */}
              <div className="ledger-table-wrap">
                {ledgerLoading ? (
                  <div style={{ padding: '40px', textAlign: 'center', color: '#64748b' }}>Loading ledger statement...</div>
                ) : !ledgerData?.entries || ledgerData.entries.length === 0 ? (
                  <div style={{ padding: '40px', textAlign: 'center', color: '#64748b' }}>
                    No financial debits or credit payments recorded for this party in the selected date range.
                  </div>
                ) : (
                  <table className="ledger-table">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Type</th>
                        <th>Ref #</th>
                        <th>Particulars / Notes</th>
                        <th style={{ textAlign: 'right' }}>Debit (₹)</th>
                        <th style={{ textAlign: 'right' }}>Credit (₹)</th>
                        <th style={{ textAlign: 'right' }}>Balance Due (₹)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {ledgerData.entries.map((entry, idx) => (
                        <tr key={idx}>
                          <td>{new Date(entry.transactionDate).toLocaleDateString('en-IN')}</td>
                          <td>
                            <span style={{
                              fontWeight: 700,
                              fontSize: '0.75rem',
                              padding: '2px 6px',
                              borderRadius: '4px',
                              background: entry.entryType === 'INVOICE' ? '#fef2f2' : '#f0fdf4',
                              color: entry.entryType === 'INVOICE' ? '#dc2626' : '#16a34a'
                            }}>
                              {entry.entryType}
                            </span>
                          </td>
                          <td style={{ fontFamily: 'monospace', fontWeight: 600 }}>{entry.referenceNumber}</td>
                          <td style={{ color: '#475569' }}>{entry.notes || '—'}</td>
                          <td style={{ textAlign: 'right', fontWeight: 600, color: '#dc2626' }}>
                            {entry.debitAmount > 0 ? `₹${parseFloat(entry.debitAmount).toLocaleString('en-IN', { minimumFractionDigits: 2 })}` : '—'}
                          </td>
                          <td style={{ textAlign: 'right', fontWeight: 600, color: '#16a34a' }}>
                            {entry.creditAmount > 0 ? `₹${parseFloat(entry.creditAmount).toLocaleString('en-IN', { minimumFractionDigits: 2 })}` : '—'}
                          </td>
                          <td style={{ textAlign: 'right', fontWeight: 700, color: '#0f172a' }}>
                            ₹{parseFloat(entry.runningBalance || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>

            <div className="party-cred-modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => window.print()}
              >
                🖨️ Print Khata Statement
              </button>
              <button
                type="button"
                className="btn btn-success"
                style={{ background: '#10b981', borderColor: '#059669', color: '#ffffff', fontWeight: 600 }}
                onClick={() => handleOpenAdvanceModal(ledgerModalTarget)}
              >
                ⚡ + Deposit Advance / Recharge Quota
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => setLedgerModalTarget(null)}
              >
                Close Statement
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Advance Deposit / Wallet Recharge Modal */}
      {advanceModalTarget && (
        <div className="party-cred-modal-overlay" onClick={() => !savingAdvance && setAdvanceModalTarget(null)}>
          <div className="party-cred-modal" style={{ maxWidth: '560px' }} onClick={(e) => e.stopPropagation()}>
            <div className="party-cred-modal-header">
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <span style={{ fontSize: '1.4rem' }}>💳</span>
                <div>
                  <h3 className="party-cred-modal-title">Record Advance Deposit / Wallet Recharge</h3>
                  <div style={{ fontSize: '0.8rem', color: '#64748b' }}>
                    {advanceModalTarget.partyName} ({advanceModalTarget.partyCode})
                  </div>
                </div>
              </div>
              <button
                type="button"
                className="party-cred-modal-close"
                onClick={() => !savingAdvance && setAdvanceModalTarget(null)}
                disabled={savingAdvance}
              >
                &times;
              </button>
            </div>

            <form onSubmit={handleCreateAdvance}>
              <div className="party-cred-modal-body">
                {/* Current Exposure Summary Banner */}
                <div style={{
                  background: '#f8fafc',
                  border: '1px solid #e2e8f0',
                  borderRadius: '8px',
                  padding: '12px 14px',
                  marginBottom: '16px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}>
                  <div>
                    <div style={{ fontSize: '0.75rem', color: '#64748b', fontWeight: 600 }}>CURRENT AVAILABLE HEADROOM</div>
                    <div style={{ fontSize: '1.2rem', fontWeight: 800, color: (advanceModalTarget.availableCredit || 0) > 0 ? '#16a34a' : '#dc2626' }}>
                      ₹{parseFloat(advanceModalTarget.availableCredit || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                    </div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: '0.75rem', color: '#64748b', fontWeight: 600 }}>POLICY</div>
                    <span className={`badge-policy ${(advanceModalTarget.creditPolicy || 'PREPAID').toLowerCase()}`}>
                      {advanceModalTarget.creditPolicy || 'PREPAID'}
                    </span>
                  </div>
                </div>

                {/* Amount with quick chips */}
                <div className="party-field-group">
                  <label className="party-field-label">Deposit Amount (₹) <span className="req">*</span></label>
                  <input
                    type="number"
                    min="1"
                    step="0.01"
                    className="party-field-input"
                    value={advanceAmount}
                    onChange={(e) => setAdvanceAmount(e.target.value)}
                    required
                    style={{ fontSize: '1.15rem', fontWeight: 700, color: '#0f172a' }}
                  />
                  <div style={{ display: 'flex', gap: '6px', marginTop: '6px' }}>
                    {[5000, 10000, 20000, 50000].map(slab => (
                      <button
                        key={slab}
                        type="button"
                        className="btn btn-secondary"
                        style={{
                          padding: '3px 8px',
                          fontSize: '0.75rem',
                          background: parseFloat(advanceAmount) === slab ? '#eff6ff' : '#ffffff',
                          borderColor: parseFloat(advanceAmount) === slab ? '#3b82f6' : '#cbd5e1',
                          color: parseFloat(advanceAmount) === slab ? '#1d4ed8' : '#475569',
                          fontWeight: 600
                        }}
                        onClick={() => setAdvanceAmount(slab)}
                      >
                        +₹{slab.toLocaleString('en-IN')}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="party-grid-2" style={{ marginTop: '12px' }}>
                  <div className="party-field-group">
                    <label className="party-field-label">Payment Mode <span className="req">*</span></label>
                    <select
                      className="party-field-input"
                      value={advanceMode}
                      onChange={(e) => setAdvanceMode(e.target.value)}
                    >
                      <option value="UPI">UPI (Google Pay / PhonePe / Paytm)</option>
                      <option value="NEFT">NEFT / RTGS Bank Transfer</option>
                      <option value="IMPS">IMPS Instant Transfer</option>
                      <option value="CHEQUE">Cheque / Demand Draft</option>
                      <option value="CASH">Cash Deposit</option>
                      <option value="CARD">Debit / Credit Card</option>
                    </select>
                  </div>

                  <div className="party-field-group">
                    <label className="party-field-label">Transaction Ref / UTR / Cheque #</label>
                    <input
                      type="text"
                      className="party-field-input"
                      placeholder="e.g. UTR2026101089324"
                      value={advanceRef}
                      onChange={(e) => setAdvanceRef(e.target.value)}
                    />
                  </div>
                </div>

                <div className="party-grid-2" style={{ marginTop: '12px' }}>
                  <div className="party-field-group">
                    <label className="party-field-label">Drawee Bank Name (Optional)</label>
                    <input
                      type="text"
                      className="party-field-input"
                      placeholder="e.g. HDFC Bank, ICICI Bank"
                      value={advanceBank}
                      onChange={(e) => setAdvanceBank(e.target.value)}
                    />
                  </div>

                  <div className="party-field-group">
                    <label className="party-field-label">Accounting Notes / Remarks</label>
                    <input
                      type="text"
                      className="party-field-input"
                      value={advanceNotes}
                      onChange={(e) => setAdvanceNotes(e.target.value)}
                    />
                  </div>
                </div>

                {/* Instant Quota Simulation */}
                <div style={{
                  marginTop: '16px',
                  background: '#f0fdf4',
                  border: '1px solid #bbf7d0',
                  borderRadius: '8px',
                  padding: '10px 14px',
                  fontSize: '0.82rem',
                  color: '#166534'
                }}>
                  ✨ <strong>Quota Impact:</strong> Upon posting, an official advance receipt (<code>ADV/YYYY/XXXXXX</code>) will be generated. The party's available booking headroom will immediately increase by <strong>₹{parseFloat(advanceAmount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</strong>, instantly unblocking sample accession!
                </div>
              </div>

              <div className="party-cred-modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setAdvanceModalTarget(null)}
                  disabled={savingAdvance}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  style={{ background: '#10b981', borderColor: '#059669' }}
                  disabled={savingAdvance}
                >
                  {savingAdvance ? 'Recording Advance...' : `Credit ₹${parseFloat(advanceAmount || 0).toLocaleString('en-IN')} to Wallet`}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Confirm Modal */}
      {confirmTarget && (
        <ConfirmModal
          isOpen={true}
          title={`${confirmTarget.nextStatus ? 'Activate' : 'Deactivate'} Party`}
          message={`Are you sure you want to ${confirmTarget.nextStatus ? 'activate' : 'deactivate'} client "${confirmTarget.partyName}"?`}
          confirmLabel={confirmTarget.nextStatus ? 'Activate' : 'Deactivate'}
          confirmVariant={confirmTarget.nextStatus ? 'primary' : 'danger'}
          onConfirm={confirmStatusChange}
          onCancel={() => setConfirmTarget(null)}
        />
      )}
    </MasterPageWrapper>
  );
};

export default PartyMasterPage;
